/**
 * Workflows P5-6: the Show article card (`show_article`) and the Add summary note step (`add_summary_note`). Merged into
 * `engage.workflows` (steps, summaries, incomplete, channel notes, Add-step keywords, problems, run reasons, analysis
 * outcomes) and kept whole at `engage.workflows.article` and `engage.workflows.summaryNote`.
 */
export const workflowsContent = {
  steps: { show_article: "Show article", add_summary_note: "Add summary note" },
  summaries: {
    show_article: "{{source}}",
    add_summary_note: "AI summary of the conversation",
    add_summary_note_notify: "AI summary of the conversation, tells the assignee",
  },
  incomplete: { article: "Choose the article to show" },
  keywords: {
    show_article: "article, help article, knowledge, link, website, page, guide, card",
    add_summary_note: "summary, summarise, summarize, ai note, recap, internal note",
  },
  problems: { no_summary_here: "This trigger starts before a conversation exists, so there is nothing to summarise." },
  problemParts: { label: "button label" },
  translationKinds: { label: "Button" },
  reasons: {
    audience: "The source is not open to this visitor",
    source_unavailable: "The source is gone, not ready or no longer public",
    too_short: "The conversation was too short to summarise",
    summary_rate_limited: "Too many summaries for this conversation or workspace this hour",
    no_credits: "No AI credits left",
    no_conversation: "There is no conversation yet",
    plan: "The plan no longer includes this step",
    summary_unavailable: "No model was available",
    already_done: "This step already wrote its note",
    preview: "Playground: nothing is written or charged",
    provider_error: "The model did not answer in time",
    empty_summary: "The model's answer had nothing usable in it",
  },
  outcomes: { shown: "Shown", written: "Note written", errored: "Could not write" },
  channelNotes: {
    steps: {
      show_article: {
        web: "Web chat, web app and mobile: a card with the title, your text and a button that opens the link in a new tab.",
        messenger: "Messenger: a message with one button that opens the link. The button title is cut to 20 characters.",
        email: "Email: the link is written in the message, with the title as its text.",
        api: "Developer API: a line with the title and the address, such as “Tuition plans: https://…”.",
      },
      add_summary_note: { behind: "Works behind the scenes on every channel. The note is for your team in the Inbox and is never sent to the visitor." },
    },
  },
  article: {
    intro: "Shows a card that links a page from your Knowledge. It opens in a new tab.",
    previewTitle: "Article title",
    preview: "How the card looks",
    text: "Text on the card",
    textHint: "Optional. A line that says what the article is about.",
    label: "Button label",
    labelHint: "Optional. “Open article” when empty.",
    audience: "Only visitors the source is open to see the card. If the source is internal, deleted or no longer public, nothing is shown and the workflow carries on.",
    source: {
      loading: "Loading sources",
      label: "Knowledge source",
      hint: "Websites with a public https address. A file lives in private storage and has no link a visitor could open.",
      choose: "Choose a source",
      search: "Search sources",
      searchPlaceholder: "Search by title or address",
      noMatches: "No source matches “{{query}}”.",
      unavailableOption: "Unavailable source",
      unavailable: "This source is gone, not ready or has no public link. Choose another.",
      loadFailedTitle: "We couldn't load the sources",
      loadFailedBody: "Check your connection and try again.",
      retry: "Try again",
      noAccess: "You need permission to view Knowledge to see the sources. Ask an admin to choose one.",
      emptyTitle: "No source with a public link yet",
      emptyBody: "Add a website in Knowledge and wait until it is ready. Files stay private, so a card can't link them.",
      openKnowledge: "Open Knowledge",
    },
    page: {
      loading: "Loading pages",
      label: "A page of the site",
      hint: "Optional. The card then shows that page's title and opens its address.",
      wholeSite: "The site's main link",
      search: "Search pages",
      searchPlaceholder: "Search by title or address",
      noMatches: "No page matches “{{query}}”.",
      unavailableOption: "Unavailable page",
      none: "This site has no crawled pages to choose from. The card links the site's own address.",
      loadFailed: "We couldn't load the pages.",
      retry: "Try again",
    },
  },
  summaryNote: {
    badge: "AI",
    planBadge: "{{plan}} plan",
    plan: "The summary note needs the {{plan}} plan or higher.",
    explainer: "Writes an AI summary of the conversation as an internal note. Uses {{credits}} AI credit. Skipped when the conversation has fewer than {{min}} messages.",
    notify: "Tell the assignee",
    notifyHint: "Sends the person the conversation is assigned to a notification.",
    privacy: "Personal details such as emails and phone numbers are removed before the conversation reaches the model. The note is only for your team.",
  },
} as const;
